import React, { useState } from "react";
import { Plus, Phone, Clock, Truck, Calendar as CalIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

var eventTypes = [
  { id: "call", label: "Llamada", icon: Phone, color: "bg-blue-500" },
  { id: "followup", label: "Seguimiento", icon: Clock, color: "bg-yellow-500" },
  { id: "delivery", label: "Entrega", icon: Truck, color: "bg-green-500" },
];

function CalendarPage({ events, clients, onAddEvent }) {
  var _useState = useState(new Date());
  var currentDate = _useState[0];
  var setCurrentDate = _useState[1];

  var _useState2 = useState(false);
  var showModal = _useState2[0];
  var setShowModal = _useState2[1];

  var _useState3 = useState({ title: "", date: "", type: "call", clientId: "" });
  var form = _useState3[0];
  var setForm = _useState3[1];

  var year = currentDate.getFullYear();
  var month = currentDate.getMonth();
  var firstDay = new Date(year, month, 1).getDay();
  var daysInMonth = new Date(year, month + 1, 0).getDate();

  var monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  var dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  function prevMonth() {
    setCurrentDate(new Date(year, month - 1, 1));
  }

  function nextMonth() {
    setCurrentDate(new Date(year, month + 1, 1));
  }

  function openNew(day) {
    var dateStr = year + "-" + String(month + 1).padStart(2, "0") + "-" + String(day).padStart(2, "0");
    setForm({ title: "", date: dateStr, type: "call", clientId: "" });
    setShowModal(true);
  }

  function handleSave() {
    if (!form.title || !form.date) {
      toast.error("Título y fecha son obligatorios");
      return;
    }
    onAddEvent({ ...form, clientId: form.clientId ? parseInt(form.clientId) : null });
    toast.success("Evento creado");
    setShowModal(false);
  }

  function getEventsForDay(day) {
    var dateStr = year + "-" + String(month + 1).padStart(2, "0") + "-" + String(day).padStart(2, "0");
    return events.filter(function(e) { return e.date === dateStr; });
  }

  // Build calendar grid
  var days = [];
  var startOffset = firstDay === 0 ? 6 : firstDay - 1; // Adjust for Monday start
  for (var i = 0; i < startOffset; i++) {
    days.push(null);
  }
  for (var d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  var today = new Date();
  var todayStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");

  return (
    <div className="h-screen overflow-y-auto p-8 bg-slate-50" data-testid="calendar-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Calendario</h1>
          <p className="text-slate-500">Gestión de citas, seguimientos y entregas</p>
        </div>
        <Button onClick={function() { setShowModal(true); }} className="btn-primary gap-2">
          <Plus size={18} /> Nuevo Evento
        </Button>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-slate-50">
          <Button variant="ghost" onClick={prevMonth}><ChevronLeft size={20} /></Button>
          <h2 className="text-lg font-bold text-slate-800">{monthNames[month]} {year}</h2>
          <Button variant="ghost" onClick={nextMonth}><ChevronRight size={20} /></Button>
        </div>

        {/* Days Header */}
        <div className="grid grid-cols-7 border-b">
          {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map(function(day) {
            return (
              <div key={day} className="p-3 text-center text-sm font-medium text-slate-500 border-r last:border-r-0">
                {day}
              </div>
            );
          })}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days.map(function(day, idx) {
            if (day === null) {
              return <div key={"empty-" + idx} className="h-28 border-r border-b bg-slate-50"></div>;
            }
            var dayEvents = getEventsForDay(day);
            var dateStr = year + "-" + String(month + 1).padStart(2, "0") + "-" + String(day).padStart(2, "0");
            var isToday = dateStr === todayStr;

            return (
              <div 
                key={day} 
                className={"h-28 border-r border-b p-2 hover:bg-slate-50 cursor-pointer transition-colors " + (isToday ? "bg-blue-50" : "")}
                onClick={function() { openNew(day); }}
              >
                <div className={"text-sm font-medium mb-1 " + (isToday ? "text-blue-600" : "text-slate-600")}>
                  {day}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map(function(event) {
                    var type = eventTypes.find(function(t) { return t.id === event.type; });
                    return (
                      <div key={event.id} className={"text-xs p-1 rounded truncate text-white " + (type ? type.color : "bg-slate-500")}>
                        {event.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 2 && (
                    <div className="text-xs text-slate-400">+{dayEvents.length - 2} más</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Events List */}
      <div className="mt-8 bg-white rounded-xl border shadow-sm p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Próximos Eventos</h3>
        <div className="space-y-3">
          {events.filter(function(e) { return e.date >= todayStr; }).sort(function(a, b) { return a.date.localeCompare(b.date); }).slice(0, 5).map(function(event) {
            var type = eventTypes.find(function(t) { return t.id === event.type; });
            var Icon = type ? type.icon : CalIcon;
            var client = clients.find(function(c) { return c.id === event.clientId; });
            return (
              <div key={event.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div className={"p-2 rounded-lg text-white " + (type ? type.color : "bg-slate-500")}>
                  <Icon size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{event.title}</p>
                  {client && <p className="text-sm text-slate-500">{client.name}</p>}
                </div>
                <span className="text-sm text-slate-400">
                  {new Date(event.date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={function(e) { setForm({ ...form, title: e.target.value }); }} placeholder="Llamar a cliente..." className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha *</Label>
                <Input type="date" value={form.date} onChange={function(e) { setForm({ ...form, date: e.target.value }); }} className="mt-1" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={function(v) { setForm({ ...form, type: v }); }}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(function(t) { return <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Cliente (opcional)</Label>
              <Select value={form.clientId} onValueChange={function(v) { setForm({ ...form, clientId: v }); }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent>
                  {clients.map(function(c) { return <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowModal(false); }}>Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary">Crear Evento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CalendarPage;
